import { type FooterMoreRowDescriptor } from '../footer-rail/footer-rail-context';
import {
  PRIMARY_NAV_COLLAPSE_ORDER,
  PRIMARY_NAV_DISPLAY_ORDER,
  PRIMARY_NAV_GAP_PX,
  PRIMARY_NAV_HYSTERESIS_PX,
  PRIMARY_NAV_ITEM_ESTIMATE_PX,
  type PrimaryNavRailId,
} from './primary-nav-rail.constants';

export interface PrimaryNavMeasurement {
  hasMeasured: boolean;
  availableHeight: number;
  heights: ReadonlyMap<PrimaryNavRailId, number>;
  moreHeight: number;
}

export interface SplitPrimaryNavRailResult {
  collapsedIds: ReadonlySet<PrimaryNavRailId>;
  moreDescriptors: readonly FooterMoreRowDescriptor[];
}

/**
 * Decides which primary-nav items collapse into More.
 * Bottom items (calls) collapse first; Home collapses last.
 * Mirrors splitFooterRail.
 */
export function splitPrimaryNavRail(
  allDescriptors: readonly FooterMoreRowDescriptor[],
  measurement: PrimaryNavMeasurement,
  wasOverflowing: boolean
): SplitPrimaryNavRailResult {
  const empty: SplitPrimaryNavRailResult = {
    collapsedIds: new Set(),
    moreDescriptors: [],
  };

  if (!measurement.hasMeasured) return empty;

  const descriptorMap = new Map(allDescriptors.map((d) => [d.id as PrimaryNavRailId, d]));
  const presentIds = PRIMARY_NAV_COLLAPSE_ORDER.filter((id) => descriptorMap.has(id));

  const heightOf = (id: PrimaryNavRailId) =>
    measurement.heights.get(id) ?? PRIMARY_NAV_ITEM_ESTIMATE_PX;

  const totalHeight = presentIds.reduce(
    (sum, id, i) => sum + heightOf(id) + (i ? PRIMARY_NAV_GAP_PX : 0),
    0
  );

  // More is always rendered — reserve its height from the budget upfront.
  const budget = measurement.availableHeight - measurement.moreHeight;
  const fitThreshold = wasOverflowing ? budget - PRIMARY_NAV_HYSTERESIS_PX : budget;

  if (totalHeight <= fitThreshold) return empty;

  // Collapse bottom-to-top until fixed items fit within the remaining budget.
  const collapsedIds = new Set<PrimaryNavRailId>();
  let used = totalHeight;

  for (const id of PRIMARY_NAV_COLLAPSE_ORDER) {
    if (!descriptorMap.has(id)) continue;
    if (used <= fitThreshold) break;
    used -= heightOf(id) + PRIMARY_NAV_GAP_PX;
    collapsedIds.add(id);
  }

  // More dropdown lists collapsed items in display order (top-to-bottom)
  const moreDescriptors = PRIMARY_NAV_DISPLAY_ORDER.filter((id) => collapsedIds.has(id))
    .map((id) => descriptorMap.get(id)!)
    .filter(Boolean);

  return { collapsedIds, moreDescriptors };
}
